import React, { useCallback, useEffect, useRef } from 'react';
import { isEmpty } from 'lodash/fp';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    WarehousePermission,
    useGetInitInfoQuery,
    usePaginatedInventoryCountItemsQuery,
    useStockCardCategoriesQuery,
} from '../../../../generated/graphql';
import { Table, TablePaginationWithCount } from '../../../../ui-kit';
import { checkAccess, translate } from '../../../../utils';
import { Input } from '../../../../ui-kit/TextField/TextField';
import { MenuListProps } from '../../../../constants';
import { InventoryCountDetailsRow, inventoryCountDetailsHeadCells } from './utils';
import useSearchParams from './useSearchParams';
import InventoryCountTableContent from './InventoryCountTableContent';
import InventoryCountTableHeadCell from './InventoryCountTableHeadCell';


interface InventoryCountDetailsTableProps {
    inventoryCountId: string;
}

type FilterParams = 'unmatchedOnly' | 'notCountedOnly' | 'moreThanExpectedOnly' | 'lessThanExpectedOnly' | null;

const filterItems = [
    {
        value: 'null',
        translation: null,
    },
    {
        value: 'unmatchedOnly',
        translation: translate('Unmatched'),
    },
    {
        value: 'notCountedOnly',
        translation: translate('Not counted'),
    },
    {
        value: 'moreThanExpectedOnly',
        translation: translate('More than expected'),
    },
    {
        value: 'lessThanExpectedOnly',
        translation: translate('Less than expected'),
    },
];

const InventoryCountDetailsTable = ({ inventoryCountId }: InventoryCountDetailsTableProps) => {
    const { isExact } = useRouteMatch();
    const { data: initData } = useGetInitInfoQuery({ fetchPolicy: 'cache-only' });
    const { data: stockCardCategoriesData } = useStockCardCategoriesQuery();
    const { t } = useTranslation();

    const warehouseCategories = stockCardCategoriesData ? stockCardCategoriesData.stockCardCategories : [];
    const {
        page, setPage,
        rowsPerPage, setRowsPerPage,
        stockCardCategoryId, setStockCardCategoryId,
        stockCardNameOrSku, setStockCardNameOrSku,
        filter, setFilter,
    } = useSearchParams();

    const offset = page * rowsPerPage;
    const filterParam = filter as FilterParams;
    const { data, loading, refetch: refetchInvenntoryCountitems } = usePaginatedInventoryCountItemsQuery({
        variables: {
            offset,
            limit: rowsPerPage,
            inventoryCountId,
            stockCardCategoryId,
            stockCardNameOrSku,
            ...(filterParam !== null && { [filterParam]: true } )
        }
    });

    const refetchQuery = useCallback(
        async () => {
            await refetchInvenntoryCountitems();
        },
        // eslint-disable-next-line
        [],
    );

    const prevIsExact = useRef(isExact);
    useEffect(() => {
        if (!prevIsExact.current) {
            // eslint-disable-next-line no-void
            void refetchQuery();

        }
        prevIsExact.current = isExact;
    // eslint-disable-next-line
    }, [isExact]);

    useEffect(() => {
        setPage(0);
    // eslint-disable-next-line
    }, [rowsPerPage, stockCardCategoryId, filter, stockCardNameOrSku]);

    const tablePagination: TablePaginationWithCount = {
        onChangePage: (_, pageNumber) => setPage(pageNumber),
        currentPage: page,
        onChangeRowsPerPage: e => setRowsPerPage(parseInt(e.target.value, 10)),
        rowsPerPage,
        count: data?.paginatedInventoryCountItems.count || 0,
    };

    const inventoryCountItems = data ? data.paginatedInventoryCountItems.items : [];

    const canViewExpectedCount = initData
        ? checkAccess({ warehouse: [WarehousePermission.CanViewInventoryCountItemExpectedCount] }, initData.employeePermissions)
        : false;

    const filteredInventoryCountDetailsHeadCells = inventoryCountDetailsHeadCells.filter(({ id }) => {
        if (id === 'expectedCount') {
            return canViewExpectedCount;
        }
        if (id === 'expectedValuation') {
            return canViewExpectedCount;
        }
        return true;
    });
    const handleFilterChange = (event: React.ChangeEvent<{
        name?: string | undefined;
        value: unknown;
    }>) => {
        const val = event.target.value as string;
        const finalValue = (val as string) === 'null' ? null : val;
        setFilter(finalValue);
    };

    const menuItems = filterItems.map(({ value, translation }) =>
        translation
            ? <MenuItem key={value} value={value}>{t(...translation)}</MenuItem>
            : <MenuItem key={value} value={value}>&mdash;</MenuItem>);

    return (
        <Box width="100%">
            <Box maxWidth='320px' mx={2}>
                <FormControl fullWidth margin='normal'>
                    <InputLabel style={{ marginLeft: '6px' }} shrink>
                        {t('Filter by').toUpperCase()}
                    </InputLabel>
                    <Select
                        value={filter === null ? null : filter}
                        onChange={handleFilterChange}
                        input={<Input />}
                        MenuProps={MenuListProps}
                    >
                        {menuItems}
                    </Select>
                </FormControl>
            </Box>
            <Table<InventoryCountDetailsRow>
                headCells={filteredInventoryCountDetailsHeadCells}
                customHeadCellProps={{
                    stockCardCategoryId, setStockCardCategoryId, warehouseCategories,
                    stockCardNameOrSku, setStockCardNameOrSku,
                }}
                TableHeadCellComponent={InventoryCountTableHeadCell}
                isFirstColumnSticky
                fixedLayout
                tablePagination={tablePagination}
            >
                <InventoryCountTableContent
                    rowsPerPage={rowsPerPage}
                    isLoading={loading && isEmpty(inventoryCountItems)}
                    headCells={filteredInventoryCountDetailsHeadCells}
                    inventoryCountItems={inventoryCountItems}
                />
            </Table>
        </Box>
    );
};

export default InventoryCountDetailsTable;
